import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Search, MapPin, Briefcase, GraduationCap, ChevronRight, User, X, Linkedin, CheckCircle, Star, MessageSquare, ArrowLeft } from 'lucide-react';
import api from '../api';
import './BrowseProfessors.css';

const BrowseProfessors = () => {
    const [professors, setProfessors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDomain, setSelectedDomain] = useState('All');
    const [selectedProfessor, setSelectedProfessor] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const fetchProfessors = async () => {
            try {
                const res = await api.get('/public-browse/professors');
                setProfessors(res.data);
            } catch (err) {
                console.error("Failed to fetch professors", err);
            } finally {
                setLoading(false);
            }
        };
        fetchProfessors();
    }, []);

    const domains = ['All', ...new Set(professors.map(p => p.domain).filter(Boolean))];

    const filteredProfessors = professors.filter(p => {
        const matchesSearch = p.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.job_title && p.job_title.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.company && p.company.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesDomain = selectedDomain === 'All' || p.domain === selectedDomain;
        return matchesSearch && matchesDomain;
    });

    return (
        <section className="browse-prof-section">
            <div className="container">
                <div className="top-navigation-alt">
                    <Link
                        to="/"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            textDecoration: 'none',
                            color: 'var(--text-faint)',
                            fontSize: '14px',
                            fontWeight: '600',
                            transition: 'color 0.2s',
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#ffffff'}
                        onMouseLeave={(e) => e.target.style.color = 'var(--text-faint)'}
                    >
                        <ArrowLeft size={16} /> Back to Home
                    </Link>
                </div>

                <div className="browse-header">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="section-title-alt"
                    >
                        Browse <span className="text-highlight">Industry Experts</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="section-subtitle-alt"
                    >
                        Vetted professionals ready to share their expertise with your institution.
                    </motion.p>
                </div>

                <div className="browse-controls-alt">
                    <div className="search-wrapper-alt">
                        <Search className="search-icon-alt" size={20} />
                        <input
                            type="text"
                            placeholder="Search by name, title or company..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="domain-filters-alt">
                        {domains.map(domain => (
                            <button
                                key={domain}
                                className={`domain-pill-alt ${selectedDomain === domain ? 'active' : ''}`}
                                onClick={() => setSelectedDomain(domain)}
                            >
                                {domain}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="loading-container">
                        <div className="spinner-alt"></div>
                        <p>Finding the right experts for you...</p>
                    </div>
                ) : (
                    <div className="prof-grid-modern-alt">
                        {filteredProfessors.length > 0 ? (
                            filteredProfessors.map((p, i) => (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="prof-card-alt"
                                >
                                    <div className="card-top-header">
                                        <div className="prof-avatar-box">
                                            {p.profile_photo_path ? (
                                                <img
                                                    src={p.profile_photo_path.startsWith('http') ? p.profile_photo_path : `${process.env.REACT_APP_API_URL || ''}${p.profile_photo_path.startsWith('/') ? '' : '/'}${p.profile_photo_path}`}
                                                    alt={p.full_name}
                                                />
                                            ) : (
                                                <div className="avatar-placeholder">
                                                    <User size={32} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="header-text-container">
                                            <div className="name-box" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                <h3 style={{ margin: 0 }}>{p.full_name}</h3>
                                                <CheckCircle size={14} className="vetted-icon" fill="#3b82f6" color="white" />
                                                <span style={{
                                                    fontSize: '10px', 
                                                    fontWeight: 'bold', 
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    padding: '3px 8px', 
                                                    borderRadius: '12px', 
                                                    background: p.years_of_experience >= 10 ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #2563eb, #3b82f6)', 
                                                    color: 'white',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                    marginTop: '2px'
                                                }}>
                                                    {p.years_of_experience >= 10 ? 'Professor of Practice' : 'Instructor'}
                                                </span>
                                            </div>
                                            <p className="job-box">{p.job_title}</p>
                                            <p className="comp-box">{p.company}</p>
                                        </div>
                                    </div>

                                    <div className="tag-row">
                                        <span className="domain-tag-mini-alt">{p.domain || 'Expert'}</span>
                                    </div>

                                    <div className="stats-grid-alt">
                                        <div className="stat-unit">
                                            <Briefcase size={14} /> <span>{p.years_of_experience}y Experience</span>
                                        </div>
                                        <div className="stat-unit">
                                            <MapPin size={14} /> <span>{p.city || 'Bengaluru'}</span>
                                        </div>

                                        <div className="stat-unit">
                                            <GraduationCap size={14} /> <span>{p.highest_education || 'Btech'}</span>
                                        </div>
                                    </div>

                                    <div className="card-bottom-alt">
                                        <button className="view-full-details-btn" onClick={() => {
                                            setSelectedProfessor(p);
                                            setShowModal(true);
                                        }}>
                                            View Full Details <ChevronRight size={16} />
                                        </button>
                                    </div>
                                </motion.div>
                            ))
                        ) : (
                            <div className="no-results-alt">
                                <p>No experts found matching your search.</p>
                            </div>
                        )}
                    </div>
                )}

                {!loading && (
                    <div className="join-footer-alt">
                        <Link to="/register" className="join-cta-btn">Join as Professor of Practice</Link>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {showModal && selectedProfessor && (
                    <div className="prof-modal-overlay-alt" onClick={() => setShowModal(false)}>
                        <motion.div
                            className="prof-modal-container-alt"
                            onClick={e => e.stopPropagation()}
                            initial={{ opacity: 0, scale: 0.9, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 30 }}
                        >
                            <button className="modal-close-btn-alt" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>

                            <div className="modal-content-grid-alt">
                                {/* Side section */}
                                <div className="modal-side-bar">
                                    <div className="modal-avatar-large">
                                        {selectedProfessor.profile_photo_path ? (
                                            <img
                                                src={selectedProfessor.profile_photo_path.startsWith('http') ? selectedProfessor.profile_photo_path : `${process.env.REACT_APP_API_URL || ''}${selectedProfessor.profile_photo_path.startsWith('/') ? '' : '/'}${selectedProfessor.profile_photo_path}`}
                                                alt={selectedProfessor.full_name}
                                            />
                                        ) : (
                                            <div className="avatar-placeholder-large">
                                                <User size={80} />
                                            </div>
                                        )}
                                    </div>

                                    <div className="modal-header-main">
                                        <h2>{selectedProfessor.full_name}</h2>
                                        <p className="main-tagline">{selectedProfessor.job_title}</p>
                                        <p className="sub-tagline">{selectedProfessor.company}</p>
                                    </div>

                                    <div className="modal-quick-stats">
                                        <div className="quick-stat-row">
                                            <span className="q-label">EXPERIENCE</span>
                                            <span className="q-value">{selectedProfessor.years_of_experience} Years</span>
                                        </div>

                                        <div className="quick-stat-row">
                                            <span className="q-label">LOCATION</span>
                                            <span className="q-value">{selectedProfessor.city || 'Hyderabad'}</span>
                                        </div>
                                    </div>

                                    <div className="modal-social-row">
                                        {selectedProfessor.linkedin_url ? (
                                            <a
                                                className="social-icon-btn"
                                                href={selectedProfessor.linkedin_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label="LinkedIn profile"
                                            >
                                                <Linkedin size={18} />
                                            </a>
                                        ) : (
                                            <button className="social-icon-btn" disabled title="LinkedIn not provided">
                                                <Linkedin size={18} />
                                            </button>
                                        )}
                                    </div>

                                </div>

                                {/* Main Content section */}
                                <div className="modal-main-feed">
                                    <div className="feed-section">
                                        <h4>PROFESSIONAL BIOGRAPHY</h4>
                                        <div className="bio-container-alt">
                                            {/* <span className="quote-icon">"</span> */}
                                            <p>{selectedProfessor.bio}</p>
                                        </div>
                                    </div>

                                    <div className="feed-section">
                                        <h4>CORE EXPERTISE</h4>
                                        <div className="expertise-tags-alt">
                                            {selectedProfessor.skills.map(skill => (
                                                <span key={skill} className="expertise-tag-alt">{skill}</span>
                                            ))}
                                            {(!selectedProfessor.skills || selectedProfessor.skills.length === 0) && (
                                                <span className="expertise-tag-alt">{selectedProfessor.domain || 'Expertise'}</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="feed-section">
                                        <h4>ACADEMIC BACKGROUND</h4>
                                        <div className="academic-card-alt">
                                            <div className="academic-icon-box">
                                                <GraduationCap size={20} color="#3b82f6" />
                                            </div>
                                            <div className="academic-text-box">
                                                <h5>{selectedProfessor.highest_education || 'Btech'}</h5>
                                                <p>Credential Verified by EdVergenceX</p>
                                            </div>
                                        </div>
                                    </div>

                                    {selectedProfessor.feedbacks && selectedProfessor.feedbacks.length > 0 && (
                                        <div className="feed-section">
                                            <h4>LATEST FEEDBACK</h4>
                                            <div className="feedback-list-alt">
                                                {selectedProfessor.feedbacks.map((fb, idx) => (
                                                    <div key={idx} className="feedback-card-alt">
                                                        <div className="feedback-header">
                                                            <div className="feedback-rating">
                                                                <Star size={14} className="star-icon" fill="#eab308" color="#eab308" />
                                                                <span>{fb.rating}/5</span>
                                                            </div>
                                                            <span className="feedback-date">
                                                                {new Date(fb.created_at).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                        <p className="feedback-comment">"{fb.comments}"</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="modal-footer-cta-alt">
                                        <button className="connect-main-btn" onClick={() => window.location.href = '/login'}>
                                            <MessageSquare size={18} /> Request to Connect with {selectedProfessor.full_name.split(' ')[0]}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default BrowseProfessors;


